import React, { useCallback, useEffect, useMemo, useState } from "react";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import AsyncPaginatedDropDown from "../../components/Atomos/DropDown/AsyncPaginatedDropDown";
import DDMultiple, { type Option } from "../../components/Atomos/DropDown/DDMultiple";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import { ReactComponent as ListIcon } from "../../assets/images&icons/list.svg";
import { SendEmailFormData, EmailTemplate } from "./types";
import TemplateDateFlightsPicker from "./TemplateDateFlightsPicker";
import { emailTemplateMatchesByEmailQuery } from "./emailTemplateDropdownFilter";
import { associateRecipientListWithTemplate } from "../../Services/EmailDistributionList";
import { isBuiltinRecipientListId } from "./builtinRecipientLists";
import { usePaginatedEmailDistributionLists } from "./usePaginatedEmailDistributionLists";

interface SendInformationCardProps {
  formData: SendEmailFormData;
  onFormChange: (updates: Partial<SendEmailFormData>) => void;
  onCreateNewList: () => void;
  emailTemplateOptions: EmailTemplate[];
  isTemplatesLoading?: boolean;
  selectedRecipientList: { id: string; name: string } | null;
  testListMemberOptions: { id: string; name: string }[];
  isTestListMembersLoading?: boolean;
  onRecipientListTemplateLinked?: (
    listId: string,
    emailTemplateIds: string[],
  ) => void;
}

export default function SendInformationCard ({
  formData,
  onFormChange,
  onCreateNewList,
  emailTemplateOptions,
  isTemplatesLoading = false,
  selectedRecipientList,
  testListMemberOptions,
  isTestListMembersLoading = false,
  onRecipientListTemplateLinked,
}: SendInformationCardProps): React.ReactElement {
  const listIcon = <ListIcon className="h-4 w-8" />;
  const [isRecipientListOpen, setIsRecipientListOpen] = useState(false);

  const selectedTemplateName = useMemo(() => {
    const opt = emailTemplateOptions.find(
      (o) => o.id === formData.emailTemplateId,
    );
    return opt?.name || "";
  }, [formData.emailTemplateId, emailTemplateOptions]);
  const selectedTemplateOption = useMemo(
    () =>
      emailTemplateOptions.find((o) => o.id === formData.emailTemplateId) ||
      null,
    [formData.emailTemplateId, emailTemplateOptions],
  );

  const testListObjects = useMemo<Option[]>(
    () => testListMemberOptions.map((m) => ({ id: m.id, name: m.name })),
    [testListMemberOptions],
  );

  const selectedTestListMembers = useMemo<Option[]>(() => {
    const idSet = new Set(formData.testListMemberIds);
    return testListObjects.filter((o) => o.id && idSet.has(o.id));
  }, [testListObjects, formData.testListMemberIds]);

  const setTestListSelection = useCallback(
    (opts: Option[] | React.SetStateAction<Option[]>) => {
      const next =
        typeof opts === "function" ? opts(selectedTestListMembers) : opts;
      const strIds = next.map((o) => o.id || "").filter(Boolean);
      onFormChange(
        strIds.length > 0
          ? { testListMemberIds: strIds, recipientListId: "" }
          : { testListMemberIds: [] },
      );
    },
    [onFormChange, selectedTestListMembers],
  );

  const selectedTemplateEmailNumber = useMemo(
    () => String(selectedTemplateOption?.emailNumber ?? "").trim(),
    [selectedTemplateOption],
  );

  const isListSectionDisabled = !formData.emailTemplateId;
  const hasTestListSelection = formData.testListMemberIds.length > 0;
  const hasRecipientListSelection = Boolean(formData.recipientListId);
  const isRecipientListDisabled =
    isListSectionDisabled || hasTestListSelection;
  const isTestListDisabled =
    isListSectionDisabled ||
    isTestListMembersLoading ||
    testListMemberOptions.length === 0 ||
    hasRecipientListSelection;

  const recipientListDropdown = usePaginatedEmailDistributionLists({
    enabled: !isListSectionDisabled && !hasTestListSelection,
    isOpen: isRecipientListOpen,
    emailTemplateNumber: selectedTemplateEmailNumber,
  });
  const {
    items: recipientListItems,
    searchQuery: recipientListSearchQuery,
    setSearchQuery: setRecipientListSearchQuery,
    hasMore: recipientListHasMore,
    isLoading: isRecipientListLoading,
    isLoadingMore: isRecipientListLoadingMore,
    loadMore: loadMoreRecipientLists,
    prependItem: prependRecipientListItem,
    updateItemEmailTemplateIds,
    showViewMore: recipientListShowViewMore,
    expandToAllLists,
  } = recipientListDropdown;

  useEffect(() => {
    if (selectedRecipientList?.id) {
      prependRecipientListItem(selectedRecipientList);
    }
  }, [selectedRecipientList, prependRecipientListItem]);

  const handleRecipientListOpenChange = useCallback(
    (open: boolean) => {
      setIsRecipientListOpen(open);
      if (!open) {
        setRecipientListSearchQuery("");
      }
    },
    [setRecipientListSearchQuery],
  );

  const handleTemplateChange = (value: { id: string; name: string } | null) => {
    onFormChange({
      emailTemplateId: value?.id || "",
      recipientListId: "",
    });
  };

  const linkRecipientListToTemplate = useCallback(
    async (listId: string) => {
      if (!listId || !selectedTemplateEmailNumber || isBuiltinRecipientListId(listId)) {
        return;
      }

      const updated = await associateRecipientListWithTemplate(
        listId,
        selectedTemplateEmailNumber,
      );
      if (!updated?.id) {
        return;
      }

      const nextTemplateIds = updated.emailTemplateIds ?? [];
      updateItemEmailTemplateIds(updated.id, nextTemplateIds);
      onRecipientListTemplateLinked?.(updated.id, nextTemplateIds);
    },
    [
      onRecipientListTemplateLinked,
      selectedTemplateEmailNumber,
      updateItemEmailTemplateIds,
    ],
  );

  const handleRecipientListChange = (
    value: { id: string; name: string } | null,
  ) => {
    const id = value?.id || "";
    onFormChange({
      recipientListId: id,
      ...(id ? { testListMemberIds: [] } : {}),
    });

    if (id) {
      void linkRecipientListToTemplate(id);
    }
  };

  const selectedRecipientListValue = useMemo(() => {
    if (selectedRecipientList?.id === formData.recipientListId) {
      return selectedRecipientList;
    }
    return (
      recipientListItems.find((item) => item.id === formData.recipientListId) ||
      selectedRecipientList
    );
  }, [selectedRecipientList, formData.recipientListId, recipientListItems]);

  const recipientListPlaceholder = isListSectionDisabled
    ? "Select email template first"
    : hasTestListSelection
      ? "Clear Test List to use a recipient list"
      : isRecipientListLoading && recipientListItems.length === 0
        ? "Loading recipient lists..."
        : !isRecipientListLoading &&
          recipientListItems.length === 0 &&
          selectedTemplateEmailNumber
          ? "No recipient lists for this email template"
          : "Select a recipient list";

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-6 lg:px-8 py-5 border-b border-gray-100">
        <h3 className="text-[2rem] font-decimal font-medium leading-none text-Default">
          Step 1: Sending Information
        </h3>
      </div>

      <div className="px-6 lg:px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-5 items-end">
        <TemplateDateFlightsPicker
          value={{
            templateDatePreset: formData.templateDatePreset,
            templateDateFrom: formData.templateDateFrom,
            templateDateTo: formData.templateDateTo,
          }}
          onApply={(v) => onFormChange({ ...v, emailTemplateId: "" })}
        />

        <DropDownList
          title="By Email"
          name="send-template-select"
          titleClass="!font-semibold !leading-none"
          data={emailTemplateOptions}
          selectedObjst={selectedTemplateName}
          selectedValue={selectedTemplateOption}
          seter={handleTemplateChange}
          error={false}
          placeholder={
            isTemplatesLoading ? "Loading templates..." : "Select a template"
          }
          required
          disabled={isTemplatesLoading}
          compareBy="id"
          getid
          filterItem={(item, query) =>
            emailTemplateMatchesByEmailQuery(item as EmailTemplate, query)
          }
        />

        <AsyncPaginatedDropDown
          title="Recipient List To modify"
          name="send-recipient-list-select"
          titleClass="!font-semibold !leading-none"
          items={recipientListItems}
          selectedValue={selectedRecipientListValue}
          selectedLabel={selectedRecipientListValue?.name}
          onChange={handleRecipientListChange}
          searchQuery={recipientListSearchQuery}
          onSearchQueryChange={setRecipientListSearchQuery}
          onLoadMore={loadMoreRecipientLists}
          hasMore={recipientListHasMore}
          isLoading={isRecipientListLoading}
          isLoadingMore={isRecipientListLoadingMore}
          onOpenChange={handleRecipientListOpenChange}
          placeholder={recipientListPlaceholder}
          disabled={isRecipientListDisabled}
          showViewMore={recipientListShowViewMore}
          onViewMore={expandToAllLists}
          viewMoreLabel="View more"
        />

        <DDMultiple
          title="Test List"
          titleClass="!font-semibold !leading-none"
          objects={testListObjects}
          selectedObjst={selectedTestListMembers}
          seter={setTestListSelection}
          disabled={isTestListDisabled}
          internalSearch
          bulkActions={testListObjects.length > 0}
          onBulkSelectAll={() => {
            setTestListSelection(testListObjects);
          }}
          onBulkClearAll={() => {
            onFormChange({ testListMemberIds: [] });
          }}
          placeholder={
            isListSectionDisabled
              ? "Select email template first"
              : isTestListMembersLoading
                ? "Loading members..."
                : testListMemberOptions.length === 0
                  ? "No members available"
                  : hasRecipientListSelection
                    ? "Clear Recipient List to use Test List"
                    : "Select WSG members"
          }
          disableWrite
        />

        <div className="flex justify-end pt-4">
          <LegacyButton
            text="CREATE NEW LIST "
            color="accent"
            icon={listIcon}
            content="textIcon"
            style="filled"
            size="small"
            onClick={onCreateNewList}
            disabled={isListSectionDisabled}
          />
        </div>
      </div>
    </div>
  );
}
